/**
 * Central place to manage modals: import components and open them with
 * the right options (include/exclude routes, shouldRender).
 *
 * Add new modals here and export openXxx() functions so options stay in one place.
 */

import { modalManager } from './manager'
import type { ModalOpenOptions } from './types'
import { ProfessionalNPS } from '../ui/ProfessionalNPS'
import {
  VideoCallRatingModal,
  type VideoCallRatingModalOwnProps,
} from '../ui/VideoCallRating/VideoCallRatingModal'
import {
  wasVideoCallRatingSubmitted,
  type VideoCallSentiment,
} from '../ui/VideoCallRating/services'

// ---------------------------------------------------------------------------
// Route helpers (login is at "/")
// ---------------------------------------------------------------------------

/** Paths where the login page lives – use in excludeRoutes so modals don’t show there. */
export const LOGIN_ROUTES = ['/', '/dash']

// ---------------------------------------------------------------------------
// Professional NPS – do not show on login page
// ---------------------------------------------------------------------------

const professionalNPSOptions: ModalOpenOptions = {
  excludeRoutes: LOGIN_ROUTES,
}

/**
 * Opens the Professional NPS modal. It will not render on the login page (/).
 */
export function openProfessionalNPS(): void {
  modalManager.open(ProfessionalNPS, {}, professionalNPSOptions)
}

// ---------------------------------------------------------------------------
// Video call quality – doctor feedback (consultation screen)
// ---------------------------------------------------------------------------

const videoCallRatingOptions: ModalOpenOptions = {
  excludeRoutes: LOGIN_ROUTES,
}

export interface OpenVideoCallRatingParams {
  schedulingId: string
  initialSentiment?: VideoCallSentiment | null
  source?: 'call_end' | 'side_prompt' | 'unknown'
  /** When true, skips opening if this scheduling was already rated this session. */
  fromCallEnd?: boolean
}

/**
 * Opens the video call quality rating modal.
 */
export function openVideoCallRating(params: OpenVideoCallRatingParams): void {
  if (params.fromCallEnd && wasVideoCallRatingSubmitted(params.schedulingId)) {
    return
  }
  modalManager.open<VideoCallRatingModalOwnProps>(
    VideoCallRatingModal,
    {
      schedulingId: params.schedulingId,
      initialSentiment: params.initialSentiment ?? null,
      source: params.source ?? 'unknown',
    },
    videoCallRatingOptions
  )
}

// ---------------------------------------------------------------------------
// Add more modals below: import component, define options, export openXxx()
// ---------------------------------------------------------------------------

// Example:
// import { SomeOtherModal } from '../components/SomeOtherModal'
// const someOtherModalOptions: ModalOpenOptions = { includeRoutes: ['/dashboard'] }
// export function openSomeOtherModal(props: SomeProps): void {
//   modalManager.open(SomeOtherModal, props, someOtherModalOptions)
// }
